import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { TimeUtilsService } from '../../service/time-utils.service';
import { mondayOf } from '../utils/date-format.util';

@Component({
  selector: 'app-modern-calendar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modern-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./modern-calendar.component.css']
})
export class ModernCalendarComponent implements OnInit, OnChanges {
  private readonly timeUtils = inject(TimeUtilsService);

  @Input() initialDate: Date | null = null;
  @Input() disablePastDays: boolean = true;
  /**
   * Set of dates (YYYY-MM-DD format) that have available course sessions.
   * IMPORTANT: Angular's change detection compares inputs by reference.
   * Parent components must create a NEW Set instance when updating
   * (e.g., `new Set(dates)`) rather than modifying the existing Set.
   */
  @Input() datesWithCourses: Set<string> = new Set();
  /**
   * When set (Wochenansicht), the Monday of the week containing this date is
   * used to tint that week's 7 days ("Gewählte Woche" legend) in addition to
   * the normal single-day selection highlight. null = no week highlight.
   */
  @Input() highlightedWeekStart: Date | null = null;
  @Output() dateSelected = new EventEmitter<Date>();
  @Output() monthChanged = new EventEmitter<{ year: number; month: number }>();
  
  currentDate = new Date();
  displayMonth: number = 0;
  displayYear: number = 0;
  selectedDate: Date | null = null;
  calendarDays: CalendarDay[] = [];
  
  weekDays: string[] = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  monthNames: string[] = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  ngOnInit(): void {
    // Initialize with provided date or current date
    if (this.initialDate) {
      this.selectedDate = new Date(this.initialDate);
    } else {
      this.selectedDate = new Date();
    }
    
    this.displayMonth = this.selectedDate.getMonth();
    this.displayYear = this.selectedDate.getFullYear();
    this.generateCalendar();

    // Emit initial month for parent to fetch course dates
    this.monthChanged.emit({ year: this.displayYear, month: this.displayMonth });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Regenerate calendar when datesWithCourses or highlightedWeekStart changes
    if (
      (changes['datesWithCourses'] && !changes['datesWithCourses'].firstChange) ||
      (changes['highlightedWeekStart'] && !changes['highlightedWeekStart'].firstChange)
    ) {
      this.generateCalendar();
    }
  }

  selectDay(day: CalendarDay): void {
    // Only proceed if day is selectable
    if (day.isDisabled) {
      return;
    }

    this.selectedDate = day.date;

    // If day is from a different month, navigate to that month first
    if (!day.isCurrentMonth) {
      this.displayMonth = day.date.getMonth();
      this.displayYear = day.date.getFullYear();
      // Emit month change so parent can fetch course dates for new month
      this.monthChanged.emit({ year: this.displayYear, month: this.displayMonth });
    }

    // Emit the selected date
    this.dateSelected.emit(day.date);

    // Regenerate calendar to update selected state and show new month if changed
    this.generateCalendar();
  }

  goToPreviousMonth(): void {
    if (this.disablePastDays) {
      // Only allow going to previous month if it's not before current month
      const now = new Date();
      if (this.displayYear < now.getFullYear() || 
         (this.displayYear === now.getFullYear() && this.displayMonth <= now.getMonth())) {
        return;  // Don't go back before current month
      }
    }
    
    if (this.displayMonth === 0) {
      this.displayMonth = 11;
      this.displayYear--;
    } else {
      this.displayMonth--;
    }
    this.generateCalendar();
    this.monthChanged.emit({ year: this.displayYear, month: this.displayMonth });
  }

  goToNextMonth(): void {
    if (this.displayMonth === 11) {
      this.displayMonth = 0;
      this.displayYear++;
    } else {
      this.displayMonth++;
    }
    this.generateCalendar();
    this.monthChanged.emit({ year: this.displayYear, month: this.displayMonth });
  }

  generateCalendar(): void {
    this.calendarDays = [];
    this.highlightedWeekBounds = this.computeHighlightedWeekBounds();

    // Get first day of month
    const firstDayOfMonth = new Date(this.displayYear, this.displayMonth, 1);
    // Get the day of week (0-6 where 0 is Sunday)
    let dayOfWeek = firstDayOfMonth.getDay();
    // Adjust for Monday start (convert Sunday from 0 to 7)
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    // Calculate offset to get to Monday
    const offset = dayOfWeek - 1;
    
    // Calculate days from previous month to display
    const prevMonthLastDay = new Date(this.displayYear, this.displayMonth, 0).getDate();
    for (let i = 0; i < offset; i++) {
      const dayNum = prevMonthLastDay - offset + i + 1;
      const date = new Date(this.displayYear, this.displayMonth - 1, dayNum);
      this.addCalendarDay(date, false);
    }
    
    // Calculate days in current month
    const daysInMonth = new Date(this.displayYear, this.displayMonth + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(this.displayYear, this.displayMonth, i);
      this.addCalendarDay(date, true);
    }
    
    // Calculate days from next month to display to fill out a 6-row grid
    const totalDisplayedDays = this.calendarDays.length;
    const remainingCells = 42 - totalDisplayedDays; // 6 rows * 7 columns = 42
    
    for (let i = 1; i <= remainingCells; i++) {
      const date = new Date(this.displayYear, this.displayMonth + 1, i);
      this.addCalendarDay(date, false);
    }
  }

  private addCalendarDay(date: Date, isCurrentMonth: boolean): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Determine if date is in the past
    const isPast = date < today;
    
    // Determine if the date should be disabled (only past days when configured, not other-month days)
    const isDisabled = this.disablePastDays && isPast;
    
    // Check if this is the selected date
    const isSelected = this.selectedDate && 
      date.getDate() === this.selectedDate.getDate() &&
      date.getMonth() === this.selectedDate.getMonth() &&
      date.getFullYear() === this.selectedDate.getFullYear();
    
    // Check if this is today
    const isToday = date.getDate() === today.getDate() &&
                   date.getMonth() === today.getMonth() &&
                   date.getFullYear() === today.getFullYear();

    // Check if this date has available course sessions
    const dateKey = this.timeUtils.formatDateForApi(date);
    const hasCourseSession = this.datesWithCourses.has(dateKey);

    const inHighlightedWeek = this.isInHighlightedWeek(date);

    this.calendarDays.push({
      date,
      dayNumber: date.getDate(),
      isCurrentMonth,
      isPast,
      isDisabled,
      isSelected: isSelected || false,
      isToday,
      hasCourseSession,
      inHighlightedWeek
    });
  }

  /** Week bounds derived ONCE per generateCalendar() pass (see its top) —
   *  isInHighlightedWeek runs per calendar cell (~42×), so recomputing the
   *  Monday there would redo the same date arithmetic for every cell. */
  private highlightedWeekBounds: { startMs: number; endMs: number } | null = null;

  private computeHighlightedWeekBounds(): { startMs: number; endMs: number } | null {
    if (!this.highlightedWeekStart) return null;
    const weekStart = mondayOf(this.highlightedWeekStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return { startMs: weekStart.getTime(), endMs: weekEnd.getTime() };
  }

  private isInHighlightedWeek(date: Date): boolean {
    const bounds = this.highlightedWeekBounds;
    if (!bounds) return false;
    const dayMs = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    return dayMs >= bounds.startMs && dayMs <= bounds.endMs;
  }

  // Format date for display
  formatMonthYear(): string {
    return `${this.monthNames[this.displayMonth]} ${this.displayYear}`;
  }

  /**
   * Format aria-label for calendar day (screen reader accessibility)
   */
  formatDayAriaLabel(day: CalendarDay): string {
    const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const dayName = dayNames[day.date.getDay()];
    const monthName = this.monthNames[day.date.getMonth()];
    let label = `${dayName}, ${day.dayNumber}. ${monthName} ${day.date.getFullYear()}`;

    if (day.isToday) {
      label += ', Heute';
    }
    if (day.isSelected) {
      label += ', ausgewählt';
    }
    if (day.hasCourseSession) {
      label += ', Kurs verfügbar';
    }
    if (day.inHighlightedWeek) {
      label += ', gewählte Woche';
    }
    if (day.isDisabled) {
      label += ', nicht verfügbar';
    }

    return label;
  }

  // Go to current month
  goToToday(): void {
    // Set today's date
    const today = new Date();
    this.selectedDate = today;

    // Check if month actually changed
    const monthChanged = this.displayMonth !== today.getMonth() || this.displayYear !== today.getFullYear();

    // Update display month/year
    this.displayMonth = today.getMonth();
    this.displayYear = today.getFullYear();

    // Regenerate calendar
    this.generateCalendar();

    // Emit the selected date
    this.dateSelected.emit(today);

    // Emit month changed if we navigated to a different month
    if (monthChanged) {
      this.monthChanged.emit({ year: this.displayYear, month: this.displayMonth });
    }
  }
}

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isPast: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isToday: boolean;
  hasCourseSession: boolean;
  inHighlightedWeek: boolean;
}